import { relations, sql } from 'drizzle-orm'
import {
  boolean,
  check,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'
import { DEFAULT_TEST_TYPE } from '../../lib/constants/test-types.js'
import { coachingHomeworkProblem, coachingQuestionHistory } from './coaching.js'
import {
  editorialFormatEnum,
  problemLinkKindEnum,
  problemRelationshipTypeEnum,
  problemStatusEnum,
  problemTypeEnum,
  problemVisibilityEnum,
  relationshipStrengthEnum,
  solutionTypeEnum,
} from './enums.js'
import {
  algoInterviewExperience,
  interviewExperience,
} from './interview-experiences.js'
import { studyPlanProblem } from './study-plans.js'

// =============================================================================
// EDITORIAL BLOCK TYPES
// =============================================================================

export type MarkdownBlock = {
  type: 'markdown'
  id: string
  content: string
}

export type EditorialApproachBlock = {
  type: 'approach'
  id: string
  approachId: string
}

export type EditorialBlock = MarkdownBlock | EditorialApproachBlock

// =============================================================================
// APPROACH BLOCK TYPES
// =============================================================================

export type ApproachCodeBlock = {
  type: 'code'
  id: string
}

export type ApproachComplexityBlock = {
  type: 'complexity'
  id: string
}

export type ApproachRecipeBlock = {
  type: 'recipe'
  id: string
  recipeId: string
}

export type ApproachContentBlock =
  | MarkdownBlock
  | ApproachCodeBlock
  | ApproachComplexityBlock
  | ApproachRecipeBlock

export type VariableDefinition = { variable: string; definition: string }

// =============================================================================
// TABLE DEFINITIONS
// =============================================================================

export const problem = pgTable(
  'Problem',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    statement: text('statement'), // Markdown content for problem description
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),

    // ── Intrinsic identity (migrated off ProblemSource) ────────────────────
    slug: text('slug').notNull(), // Globally unique URL identifier
    title: text('title').notNull(), // Display name
    difficultyId: uuid('difficultyId').references(() => difficultyLevel.id, {
      onDelete: 'set null',
    }),
    // The source this problem's content belongs to (BCTCI, LeetCode, …).
    // ProblemSource rows become pure "also listed on" attribution.
    sourceId: uuid('sourceId').references(() => source.id, {
      onDelete: 'set null',
    }),
    externalId: text('externalId'), // ID in the owning source's system
    sourceUrl: text('sourceUrl'), // Link to the problem on its owning source
    editorialUrl: text('editorialUrl'),
    premiumRequired: boolean('premiumRequired').default(false).notNull(),

    // Problem-level metadata (shared across all sources)
    /**
     * Which coding surface this problem needs (LG-55). `algorithms` is today's
     * editor; `sql` and `shell` are labels on inert metadata until the content
     * and runners exist. Descriptors like concurrency live in ProblemTag.
     */
    type: problemTypeEnum('type').default('algorithms').notNull(),
    shortDescription: text('shortDescription'),

    // Statistics (aggregated, not source-specific)
    acceptanceRate: doublePrecision('acceptanceRate'),
    freqBar: doublePrecision('freqBar'),
    totalAccepted: integer('totalAccepted'),
    totalSubmitted: integer('totalSubmitted'),
    eloRating: doublePrecision('eloRating'),

    // Editorial blocks (ordered array of markdown/approach/etc. blocks)
    editorialBlocks: jsonb('editorial_blocks')
      .$type<EditorialBlock[]>()
      .default([])
      .notNull(),

    // Editorial format: 'new' (DB-driven with editorial blocks) or 'legacy' (synced from bctci-code)
    editorialFormat: editorialFormatEnum('editorialFormat')
      .default('new')
      .notNull(),

    // Pre-rendered editorial markdown for API serving (no frontmatter, no JSX)
    editorialMarkdown: text('editorial_markdown'),

    // Publishing & Access Control
    // Security: Default to most restrictive settings
    // - draft: not visible until explicitly published
    // - admin_only: even if accidentally published, only admins see it
    status: problemStatusEnum('status').default('draft').notNull(),
    visibility: problemVisibilityEnum('visibility')
      .default('admin_only')
      .notNull(),
  },
  (table) => [
    // Postgres unique constraints permit multiple NULLs, so this is safe
    // while slug is still being backfilled.
    unique('Problem_slug_unique').on(table.slug),
    index('Problem_sourceId_idx').on(table.sourceId),
  ],
)

export const difficultyLevel = pgTable('DifficultyLevel', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull().unique(),
  level: integer('level').notNull(),
  description: text('description'),
  color: text('color'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

export const approach = pgTable('Approach', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull(),
  solutionType: solutionTypeEnum('solutionType'),
  blocks: jsonb('blocks').$type<ApproachContentBlock[]>().default([]).notNull(),
  isPreferred: boolean('isPreferred').default(false).notNull(),
  tags: text('tags').array().default([]).notNull(),
  order: integer('order').default(0).notNull(),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),

  problemId: uuid('problemId')
    .notNull()
    .references(() => problem.id, { onDelete: 'cascade' }),
  difficultyId: uuid('difficultyId').references(() => difficultyLevel.id, {
    onDelete: 'set null',
  }),
})

export const solution = pgTable(
  'Solution',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    language: text('language').notNull(),
    code: text('code').notNull(),
    explanation: text('explanation'),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),

    approachId: uuid('approachId')
      .notNull()
      .references(() => approach.id, { onDelete: 'cascade' }),
  },
  (table) => [unique().on(table.approachId, table.language)],
)

export const solutionComplexity = pgTable(
  'SolutionComplexity',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    functionName: text('functionName').notNull(),
    timeComplexity: text('timeComplexity'),
    spaceComplexity: text('spaceComplexity'),
    timeComplexityExplanation: text('timeComplexityExplanation'),
    spaceComplexityExplanation: text('spaceComplexityExplanation'),
    variableDefinitions: jsonb('variableDefinitions')
      .$type<VariableDefinition[]>()
      .default([])
      .notNull(),
    overridesApproach: boolean('overridesApproach').default(false).notNull(),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),

    solutionId: uuid('solutionId')
      .notNull()
      .references(() => solution.id, { onDelete: 'cascade' }),
  },
  (table) => [index('SolutionComplexity_solutionId_idx').on(table.solutionId)],
)

export const approachComplexity = pgTable(
  'ApproachComplexity',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    functionName: text('functionName').notNull(),
    timeComplexity: text('timeComplexity'),
    spaceComplexity: text('spaceComplexity'),
    timeComplexityExplanation: text('timeComplexityExplanation'),
    spaceComplexityExplanation: text('spaceComplexityExplanation'),
    variableDefinitions: jsonb('variableDefinitions')
      .$type<VariableDefinition[]>()
      .default([])
      .notNull(),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),

    approachId: uuid('approachId')
      .notNull()
      .references(() => approach.id, { onDelete: 'cascade' }),
  },
  (table) => [index('ApproachComplexity_approachId_idx').on(table.approachId)],
)

export const company = pgTable('Company', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull().unique(),
  slug: text('slug').notNull().unique(),
  aliases: text('aliases').array().default([]).notNull(),
  imgUrl: text('imgUrl'),
  parentId: uuid('parentId'),
  description: text('description'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * Source table - normalized problem sources
 *
 * Stores canonical source information for problems (e.g., LeetCode, OpenAI Interview Experience).
 * Referenced by ProblemSource.source field for consistency.
 */
export const source = pgTable(
  'Source',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    name: text('name').unique(), // Required when companyId is null, null when companyId is set
    slug: text('slug').notNull().unique(), // "leetcode", "google-interview-experience"
    icon: text('icon'), // Icon filename in static/icons/ (e.g., "leetcode.png")
    category: text('category').notNull(), // "prep_resource" | "interview_experience"
    url: text('url'), // Optional link to source website
    companyId: uuid('companyId').references(() => company.id, {
      onDelete: 'restrict',
    }),
    // Publishing control: false for external sources (LeetCode), true for original content (BCTCI)
    isPublishable: boolean('isPublishable').default(true).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    // Exactly one of name / companyId: standalone sources store a name,
    // company-backed sources derive their display name from Company
    check(
      'Source_name_xor_companyId_check',
      sql`(${table.name} IS NULL) <> (${table.companyId} IS NULL)`,
    ),
  ],
)

/**
 * @deprecated Company associations are now derived through interview experiences:
 * Problem → AlgoInterviewExperience → InterviewQuestionReport → InterviewExperience → Company.
 * This table remains for backward compatibility in the questions router.
 * Do not use for new features.
 */
export const problemCompany = pgTable(
  'ProblemCompany',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    companyId: uuid('companyId')
      .notNull()
      .references(() => company.id, { onDelete: 'cascade' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.problemId, table.companyId),
    index('ProblemCompany_problemId_idx').on(table.problemId),
    index('ProblemCompany_companyId_idx').on(table.companyId),
  ],
)

export const companyTag = pgTable('CompanyTag', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull().unique(),
  slug: text('slug').notNull().unique(),
  description: text('description'),
  order: integer('order').notNull().default(0),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

export const companyCompanyTag = pgTable(
  'CompanyCompanyTag',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    companyId: uuid('companyId')
      .notNull()
      .references(() => company.id, { onDelete: 'cascade' }),
    companyTagId: uuid('companyTagId')
      .notNull()
      .references(() => companyTag.id, { onDelete: 'cascade' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.companyId, table.companyTagId),
    index('CompanyCompanyTag_companyId_idx').on(table.companyId),
    index('CompanyCompanyTag_companyTagId_idx').on(table.companyTagId),
  ],
)

export const hint = pgTable(
  'Hint',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    content: text('content').notNull(),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.problemId, table.order),
    index('Hint_problemId_idx').on(table.problemId),
  ],
)

/**
 * ProblemConstraint table - stores problem statement constraints
 * e.g., "1 <= n <= 10^4", "The array is sorted in non-decreasing order"
 */
export const problemConstraint = pgTable(
  'ProblemConstraint',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    content: text('content').notNull(),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.problemId, table.order),
    index('ProblemConstraint_problemId_idx').on(table.problemId),
  ],
)

/**
 * Clarification table - stores Q&A clarifications for a problem
 */
export const clarification = pgTable(
  'Clarification',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    question: text('question').notNull(),
    answer: text('answer').notNull(),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.problemId, table.order),
    index('Clarification_problemId_idx').on(table.problemId),
  ],
)

/**
 * ApproachHint table - stores hints specific to an approach
 */
export const approachHint = pgTable(
  'ApproachHint',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    approachId: uuid('approachId')
      .notNull()
      .references(() => approach.id, { onDelete: 'cascade' }),
    content: text('content').notNull(),
    order: integer('order').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.approachId, table.order),
    index('ApproachHint_approachId_idx').on(table.approachId),
  ],
)

/**
 * ProblemSource junction table - tracks attribution sources for a problem
 *
 * Problem owns its identity. ProblemSource rows are secondary "also listed on"
 * attribution metadata only.
 */
export const problemSource = pgTable(
  'ProblemSource',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    sourceId: uuid('sourceId')
      .notNull()
      .references(() => source.id, { onDelete: 'cascade' }),
    title: text('title').notNull(), // The source's name for this problem
    externalId: text('externalId'), // ID in this source's system
    premiumRequired: boolean('premiumRequired').default(false).notNull(), // Whether this source requires premium
    url: text('url'), // Link to problem on this source
    editorialUrl: text('editorialUrl'), // Link to editorial on this source
    /**
     * Marks the source the problem *originated* on, which is not the same
     * question as `Problem.sourceId` (whose content this is). They coincide
     * for imported problems, but a BCTCI-authored write-up of a well-known
     * classic is owned by BCTCI and originated elsewhere — nothing recorded
     * that before this flag. Attribution-only, so it lives here rather than
     * on `Problem`, which owns identity.
     *
     * At most one row per problem may set it; see the partial unique index
     * below. Learner-facing resolution falls back to the owning source when
     * no row is marked, so the imported corpus needs no backfill.
     */
    isOriginal: boolean('isOriginal').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('ProblemSource_problemId_sourceId_unique').on(
      table.problemId,
      table.sourceId,
    ),
    index('ProblemSource_problemId_idx').on(table.problemId),
    index('ProblemSource_sourceId_idx').on(table.sourceId),
    // Partial: only the marked rows participate, so a problem may hold many
    // unmarked sources but at most one original. Enforcing "exactly one" is
    // deliberately not attempted — most problems mark none and resolve to
    // the owning source instead.
    uniqueIndex('ProblemSource_problemId_isOriginal_unique')
      .on(table.problemId)
      .where(sql`${table.isOriginal}`),
  ],
)

/**
 * ProblemVariant table - links company-specific variant problems to their canonical source
 *
 * For example, if a company asks a variation of LeetCode's "House Robber" with
 * a different theme or additional constraints, the LeetCode version is the canonical
 * problem and the company's version is the variant.
 */
export const problemVariant = pgTable(
  'ProblemVariant',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    canonicalProblemId: uuid('canonicalProblemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    variantProblemId: uuid('variantProblemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    kind: problemLinkKindEnum('kind').default('variant').notNull(),
    notes: text('notes'), // Context about differences (e.g., "Adds max 2 consecutive constraint")
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('ProblemVariant_canonical_variant_unique').on(
      table.canonicalProblemId,
      table.variantProblemId,
    ),
    index('ProblemVariant_canonicalProblemId_idx').on(table.canonicalProblemId),
    index('ProblemVariant_variantProblemId_idx').on(table.variantProblemId),
  ],
)

/**
 * ProblemSeries table - named groupings of thematically related problems
 *
 * For example, "Best Time to Buy and Sell Stock" series contains problems I, II, III, IV
 * that share a theme but may have very different solutions.
 */
export const problemSeries = pgTable('ProblemSeries', {
  id: uuid('id').primaryKey().defaultRandom(),
  slug: text('slug').notNull().unique(),
  name: text('name').notNull(), // Display name (e.g., "Best Time to Buy and Sell Stock")
  description: text('description'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * ProblemSeriesMember junction table - links problems to series with explicit ordering
 *
 * A problem can belong to multiple series, and each series has an ordered list of problems.
 */
export const problemSeriesMember = pgTable(
  'ProblemSeriesMember',
  {
    seriesId: uuid('seriesId')
      .notNull()
      .references(() => problemSeries.id, { onDelete: 'cascade' }),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    order: integer('order').notNull(), // Position in series (1, 2, 3, 4...)
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.seriesId, table.problemId] }),
    unique('ProblemSeriesMember_seriesId_order_unique').on(
      table.seriesId,
      table.order,
    ),
    index('ProblemSeriesMember_seriesId_idx').on(table.seriesId),
    index('ProblemSeriesMember_problemId_idx').on(table.problemId),
  ],
)

/**
 * ProblemFollowUp table - intra-problem constraint extensions
 *
 * Follow-ups are constraint modifications presented AFTER a user solves the base problem.
 * Examples: "Now do it in O(1) space", "Can you solve it in a single pass?"
 * They can form a DAG with prerequisites between them for progressive unlocking.
 */
export const problemFollowUp = pgTable(
  'ProblemFollowUp',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    prompt: text('prompt').notNull(), // "Now do it in O(1) space"
    order: integer('order').default(0).notNull(), // Display ordering
    expectedTimeComplexity: text('expectedTimeComplexity'),
    expectedSpaceComplexity: text('expectedSpaceComplexity'),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [index('ProblemFollowUp_problemId_idx').on(table.problemId)],
)

/**
 * FollowUpPrereq junction table - follow-up prerequisites (AND logic)
 *
 * Links follow-ups to their prerequisite follow-ups. Uses AND logic:
 * all prerequisites must be completed before a follow-up unlocks.
 */
export const followUpPrereq = pgTable(
  'FollowUpPrereq',
  {
    followUpId: uuid('followUpId')
      .notNull()
      .references(() => problemFollowUp.id, { onDelete: 'cascade' }),
    prereqFollowUpId: uuid('prereqFollowUpId')
      .notNull()
      .references(() => problemFollowUp.id, { onDelete: 'cascade' }),
  },
  (table) => [
    primaryKey({ columns: [table.followUpId, table.prereqFollowUpId] }),
    index('FollowUpPrereq_followUpId_idx').on(table.followUpId),
    index('FollowUpPrereq_prereqFollowUpId_idx').on(table.prereqFollowUpId),
  ],
)

/**
 * FollowUpApproach junction table - links follow-ups to target approaches
 *
 * A follow-up can be satisfied by one or more approaches.
 * Example: "O(n) time" follow-up might link to Hash Table, Math, or XOR approaches.
 */
export const followUpApproach = pgTable(
  'FollowUpApproach',
  {
    followUpId: uuid('followUpId')
      .notNull()
      .references(() => problemFollowUp.id, { onDelete: 'cascade' }),
    approachId: uuid('approachId')
      .notNull()
      .references(() => approach.id, { onDelete: 'cascade' }),
  },
  (table) => [
    primaryKey({ columns: [table.followUpId, table.approachId] }),
    index('FollowUpApproach_followUpId_idx').on(table.followUpId),
    index('FollowUpApproach_approachId_idx').on(table.approachId),
  ],
)

/**
 * ProblemRelationship table - inter-problem dependency edges
 *
 * Connects different problem entries with typed relationships:
 * - prerequisite: Understanding target helps before attempting source
 * - builds_on: Source's solution extends target's solution
 * - harder_than: Source is target with harder constraints
 *
 * Multiple edges between same problems are allowed (different types).
 * Strength indicates required/recommended/optional.
 */
export const problemRelationship = pgTable(
  'ProblemRelationship',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    sourceProblemId: uuid('sourceProblemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    targetProblemId: uuid('targetProblemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    type: problemRelationshipTypeEnum('type').notNull(),
    strength: relationshipStrengthEnum('strength')
      .default('recommended')
      .notNull(),
    notes: text('notes'),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    // Allow multiple relationships between same problems (different types)
    unique('ProblemRelationship_source_target_type_unique').on(
      table.sourceProblemId,
      table.targetProblemId,
      table.type,
    ),
    index('ProblemRelationship_sourceProblemId_idx').on(table.sourceProblemId),
    index('ProblemRelationship_targetProblemId_idx').on(table.targetProblemId),
    index('ProblemRelationship_type_idx').on(table.type),
  ],
)

/**
 * TestConfiguration table - stores test schema for a problem
 *
 * One problem can have one test configuration (with input parameters and output type).
 * The configuration defines the test schema, and test cases reference this configuration.
 *
 * For class-based tests (testType = "class"):
 * - className: The class name to instantiate
 * - inputParameters: Constructor parameter definitions
 * - outputType: Not used (can be null)
 * - Test cases use the "operations" field instead of "inputs"/"expectedOutput"
 */
/**
 * InputParameter type - represents a function/constructor parameter definition
 */
export interface InputParameter {
  id?: string // Optional stable ID for UI rendering (not persisted)
  name: string
  type: string // Composable type string: "integer", "TreeNode", "integer[][]", etc.
  order: number
  inPlaceModified?: boolean // Does this param get modified in-place by the function?
  userFacing?: boolean // Defaults to true; set false for test-only params (used by custom_setup but not user function)
}

/**
 * MethodParameter - one parameter of a class-method signature
 */
export interface MethodParameter {
  name: string
  type: string // Composable type string: "integer", "TreeNode", "integer[][]", etc.
  order: number
}

/**
 * MethodSignature - typed signature for one method of a class-based problem.
 * `returns: 'void'` means the harness calls the method and ignores the return
 * value (the operation's `expected` must be null). Any other value is a
 * composable type string like outputType.
 */
export interface MethodSignature {
  name: string
  params: MethodParameter[]
  returns: string
}

/**
 * Comparison modes for test output validation
 */
export type ComparisonMode =
  | 'exact' // Default: output must match exactly
  | 'set_equality' // Order doesn't matter (e.g., [1,2,3] == [3,1,2])
  | 'sorted_equality' // Sort both before comparing

export const testConfiguration = pgTable(
  'TestConfiguration',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
    testType: text('testType').notNull().default(DEFAULT_TEST_TYPE), // "function" | "class"
    functionNames: text('functionNames').array().default([]), // For function-based tests: acceptable function name slugs (first = preferred, slug-derived always accepted)
    className: text('className'), // For class-based tests: the class name to instantiate
    inputParameters: jsonb('inputParameters').$type<InputParameter[]>(), // For function: input params. For class: constructor params.
    methods: jsonb('methods').$type<MethodSignature[]>(), // For class-based tests: typed method signatures (null = legacy untyped config)
    outputType: text('outputType'), // GenericType: "integer", "integer[]", etc. (nullable for class tests)
    // Comparison settings:
    comparisonMode: text('comparisonMode').default('exact'), // How to compare outputs
    tolerance: doublePrecision('tolerance'), // For approximate comparisons (epsilon)
    approximate: boolean('approximate').default(false), // Flag: use epsilon-based float comparison
    caseInsensitive: boolean('caseInsensitive').default(false), // Flag: normalize strings to lowercase
    // Starter code: language-keyed stubs shown in editor on first load
    starterCode: jsonb('starterCode').$type<Record<string, string>>(), // { python: "def foo(x):\n    pass\n" }
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    unique().on(table.problemId), // One test configuration per problem
    index('TestConfiguration_problemId_idx').on(table.problemId),
  ],
)

/**
 * OperationItem type - represents a single method call in a class-based test
 */
export interface OperationItem {
  type?: 'operation' // Optional for backward compatibility (missing = operation)
  method: string // Method name (e.g., "get", "put", "append")
  args: unknown[] // Arguments as JSON values
  expected: unknown | null // Expected return value (null for void methods)
  expectsError?: boolean // If true, this operation should throw an error
  propertyToCheck?: string // For in-place class methods: property to verify after call (e.g., "matrix")
  expectedPropertyValue?: unknown // Expected value of that property after call
}

/**
 * CommentItem type - represents a comment line in a class-based test's
 * operation list. Comments have no effect on test execution; they only
 * annotate the operation sequence shown in the editor.
 */
export interface CommentItem {
  type: 'comment'
  text: string // Comment text (multi-line supported with \n separators)
}

/**
 * OperationRecord type - union of operation and comment items
 * Used in class-based test cases to represent the sequence of method calls and comments.
 */
export type OperationRecord = OperationItem | CommentItem

/**
 * Type guard to check if an OperationRecord is a comment
 */
export function isCommentItem(item: OperationRecord): item is CommentItem {
  return item.type === 'comment'
}

/**
 * Type guard to check if an OperationRecord is an operation
 */
export function isOperationItem(item: OperationRecord): item is OperationItem {
  return item.type !== 'comment'
}

/**
 * TestCase table - stores individual test cases for a test configuration
 *
 * For function-based tests (testType = "function"):
 * - inputs: Object mapping parameter names to values (as JSON strings)
 * - expectedOutput: JSON string of expected value
 *
 * For class-based tests (testType = "class"):
 * - constructorArgs: Arguments for the class constructor
 * - operations: Array of method calls with expected results
 * - inputs/expectedOutput are not used
 */
export const testCase = pgTable(
  'TestCase',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    testConfigurationId: uuid('testConfigurationId')
      .notNull()
      .references(() => testConfiguration.id, { onDelete: 'cascade' }),
    // For function-based tests:
    inputs: jsonb('inputs').$type<Record<string, string>>(), // Object mapping parameter names to values (as JSON strings)
    expectedOutput: text('expectedOutput'), // JSON string of expected value (nullable for class tests)
    expectedInputsAfter: jsonb('expectedInputsAfter').$type<
      Record<string, unknown>
    >(), // For in-place modifications: expected values of modified params after call
    // For class-based tests:
    constructorArgs: jsonb('constructorArgs').$type<unknown[]>(), // Arguments for class constructor
    operations: jsonb('operations').$type<OperationRecord[]>(), // Array of method calls with expected results
    // For raw test type:
    scripts: jsonb('scripts').$type<Record<string, string>>(), // Language-keyed test scripts (e.g., { python: "...", javascript: "..." })
    exampleDisplay: text('exampleDisplay'), // Free-form text for example card display (raw mode only)
    // Common fields:
    order: integer('order').notNull(),
    intent: text('intent'), // Optional: Short rationale explaining what makes this test unique
    // Example display settings:
    showAsExample: boolean('showAsExample').default(false), // Include this test case in examples?
    // Display override fields:
    imageFilename: text('imageFilename'), // Filename from static/images/
    inputDisplayOverride: text('inputDisplayOverride'), // Formatted input for user display
    outputDisplayOverride: text('outputDisplayOverride'), // Formatted output for user display
    displayOverrideStale: boolean('displayOverrideStale').default(false), // True when inputs change after override set
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique().on(table.testConfigurationId, table.order),
    index('TestCase_testConfigurationId_idx').on(table.testConfigurationId),
  ],
)

// =============================================================================
// RELATIONS (defined after all tables)
// =============================================================================

export const problemRelations = relations(problem, ({ many, one }) => ({
  approaches: many(approach),
  questionHistory: many(coachingQuestionHistory),
  assignedHomework: many(coachingHomeworkProblem),
  companies: many(problemCompany),
  hints: many(hint),
  constraints: many(problemConstraint),

  clarifications: many(clarification),
  studyPlans: many(studyPlanProblem),
  sources: many(problemSource),
  difficulty: one(difficultyLevel, {
    fields: [problem.difficultyId],
    references: [difficultyLevel.id],
  }),
  owningSource: one(source, {
    fields: [problem.sourceId],
    references: [source.id],
  }),
  // Problem variants - this problem as canonical source
  variantsAsCanonical: many(problemVariant, { relationName: 'canonical' }),
  // Problem variants - this problem as a variant of another
  variantsAsVariant: many(problemVariant, { relationName: 'variant' }),
  // Problem series memberships
  seriesMemberships: many(problemSeriesMember),
  // Problem follow-ups (intra-problem extensions)
  followUps: many(problemFollowUp),
  // Problem relationships - this problem as source
  sourceRelationships: many(problemRelationship, {
    relationName: 'sourceRelationships',
  }),
  // Problem relationships - this problem as target
  targetRelationships: many(problemRelationship, {
    relationName: 'targetRelationships',
  }),
  // Test configuration (one per problem)
  testConfiguration: one(testConfiguration),
  interviewLinks: many(algoInterviewExperience),
}))

export const difficultyLevelRelations = relations(
  difficultyLevel,
  ({ many }) => ({
    approaches: many(approach),
    problems: many(problem),
  }),
)

export const approachRelations = relations(approach, ({ one, many }) => ({
  problem: one(problem, {
    fields: [approach.problemId],
    references: [problem.id],
  }),
  difficulty: one(difficultyLevel, {
    fields: [approach.difficultyId],
    references: [difficultyLevel.id],
  }),
  solutions: many(solution),
  hints: many(approachHint),
  recipes: many(approachRecipe),
  complexities: many(approachComplexity),
}))

export const solutionRelations = relations(solution, ({ one, many }) => ({
  approach: one(approach, {
    fields: [solution.approachId],
    references: [approach.id],
  }),
  complexities: many(solutionComplexity),
}))

export const solutionComplexityRelations = relations(
  solutionComplexity,
  ({ one }) => ({
    solution: one(solution, {
      fields: [solutionComplexity.solutionId],
      references: [solution.id],
    }),
  }),
)

export const approachComplexityRelations = relations(
  approachComplexity,
  ({ one }) => ({
    approach: one(approach, {
      fields: [approachComplexity.approachId],
      references: [approach.id],
    }),
  }),
)

export const companyRelations = relations(company, ({ one, many }) => ({
  problems: many(problemCompany),
  tags: many(companyCompanyTag),
  parent: one(company, {
    fields: [company.parentId],
    references: [company.id],
    relationName: 'companyHierarchy',
  }),
  children: many(company, { relationName: 'companyHierarchy' }),
  interviewExperiences: many(interviewExperience),
}))

export const problemCompanyRelations = relations(problemCompany, ({ one }) => ({
  problem: one(problem, {
    fields: [problemCompany.problemId],
    references: [problem.id],
  }),
  company: one(company, {
    fields: [problemCompany.companyId],
    references: [company.id],
  }),
}))

export const companyTagRelations = relations(companyTag, ({ many }) => ({
  companies: many(companyCompanyTag),
}))

export const companyCompanyTagRelations = relations(
  companyCompanyTag,
  ({ one }) => ({
    company: one(company, {
      fields: [companyCompanyTag.companyId],
      references: [company.id],
    }),
    tag: one(companyTag, {
      fields: [companyCompanyTag.companyTagId],
      references: [companyTag.id],
    }),
  }),
)

export const hintRelations = relations(hint, ({ one }) => ({
  problem: one(problem, {
    fields: [hint.problemId],
    references: [problem.id],
  }),
}))

export const problemConstraintRelations = relations(
  problemConstraint,
  ({ one }) => ({
    problem: one(problem, {
      fields: [problemConstraint.problemId],
      references: [problem.id],
    }),
  }),
)

export const clarificationRelations = relations(clarification, ({ one }) => ({
  problem: one(problem, {
    fields: [clarification.problemId],
    references: [problem.id],
  }),
}))

export const approachHintRelations = relations(approachHint, ({ one }) => ({
  approach: one(approach, {
    fields: [approachHint.approachId],
    references: [approach.id],
  }),
}))

export const problemSourceRelations = relations(problemSource, ({ one }) => ({
  problem: one(problem, {
    fields: [problemSource.problemId],
    references: [problem.id],
  }),
  source: one(source, {
    fields: [problemSource.sourceId],
    references: [source.id],
  }),
}))

export const sourceRelations = relations(source, ({ many, one }) => ({
  problemSources: many(problemSource),
  company: one(company, {
    fields: [source.companyId],
    references: [company.id],
  }),
}))

export const problemVariantRelations = relations(problemVariant, ({ one }) => ({
  canonicalProblem: one(problem, {
    fields: [problemVariant.canonicalProblemId],
    references: [problem.id],
    relationName: 'canonical',
  }),
  variantProblem: one(problem, {
    fields: [problemVariant.variantProblemId],
    references: [problem.id],
    relationName: 'variant',
  }),
}))

export const problemSeriesRelations = relations(problemSeries, ({ many }) => ({
  members: many(problemSeriesMember),
}))

export const problemSeriesMemberRelations = relations(
  problemSeriesMember,
  ({ one }) => ({
    series: one(problemSeries, {
      fields: [problemSeriesMember.seriesId],
      references: [problemSeries.id],
    }),
    problem: one(problem, {
      fields: [problemSeriesMember.problemId],
      references: [problem.id],
    }),
  }),
)

export const problemFollowUpRelations = relations(
  problemFollowUp,
  ({ one, many }) => ({
    problem: one(problem, {
      fields: [problemFollowUp.problemId],
      references: [problem.id],
    }),
    // Follow-ups that this follow-up requires (prerequisites)
    prereqs: many(followUpPrereq, { relationName: 'followUp' }),
    // Follow-ups that require this follow-up as a prerequisite
    prereqOf: many(followUpPrereq, { relationName: 'prereqFollowUp' }),
    // Approaches that satisfy this follow-up
    approaches: many(followUpApproach),
  }),
)

export const followUpPrereqRelations = relations(followUpPrereq, ({ one }) => ({
  followUp: one(problemFollowUp, {
    fields: [followUpPrereq.followUpId],
    references: [problemFollowUp.id],
    relationName: 'followUp',
  }),
  prereqFollowUp: one(problemFollowUp, {
    fields: [followUpPrereq.prereqFollowUpId],
    references: [problemFollowUp.id],
    relationName: 'prereqFollowUp',
  }),
}))

export const followUpApproachRelations = relations(
  followUpApproach,
  ({ one }) => ({
    followUp: one(problemFollowUp, {
      fields: [followUpApproach.followUpId],
      references: [problemFollowUp.id],
    }),
    approach: one(approach, {
      fields: [followUpApproach.approachId],
      references: [approach.id],
    }),
  }),
)

export const problemRelationshipRelations = relations(
  problemRelationship,
  ({ one }) => ({
    sourceProblem: one(problem, {
      fields: [problemRelationship.sourceProblemId],
      references: [problem.id],
      relationName: 'sourceRelationships',
    }),
    targetProblem: one(problem, {
      fields: [problemRelationship.targetProblemId],
      references: [problem.id],
      relationName: 'targetRelationships',
    }),
  }),
)

export const testConfigurationRelations = relations(
  testConfiguration,
  ({ one, many }) => ({
    problem: one(problem, {
      fields: [testConfiguration.problemId],
      references: [problem.id],
    }),
    testCases: many(testCase),
    // Custom test handler relations
    templateLinks: many(testConfigurationTemplate),
    customCode: many(testConfigurationCustomCode),
  }),
)

export const testCaseRelations = relations(testCase, ({ one }) => ({
  testConfiguration: one(testConfiguration, {
    fields: [testCase.testConfigurationId],
    references: [testConfiguration.id],
  }),
}))

// =============================================================================
// TEST TEMPLATES (Custom Test Handlers)
// =============================================================================

/**
 * TestTemplate table - Reusable code templates for test harness
 *
 * Stores reusable code templates that can be shared across problems.
 * Templates provide class definitions (TreeNode, ListNode) and helper
 * functions (create_cyclic_list) for problems requiring custom data structures.
 */
export const testTemplate = pgTable('TestTemplate', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull(), // Human-readable name (e.g., "ListNode with Cycle Helper")
  slug: text('slug').notNull().unique(), // URL-safe identifier (e.g., "listnode-cycle-helper")
  description: text('description'), // Explains what the template provides
  order: integer('order').notNull().default(0), // For ordering in UI
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * TestTemplateCode table - Language-specific code for templates
 *
 * Stores the actual code for each template, normalized by language.
 * Each template can have code for multiple languages (Python, JavaScript).
 */
export const testTemplateCode = pgTable(
  'TestTemplateCode',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    templateId: uuid('templateId')
      .notNull()
      .references(() => testTemplate.id, { onDelete: 'cascade' }),
    language: text('language').notNull(), // 'python' | 'javascript'
    definitionCode: text('definitionCode').notNull().default(''), // Class/struct definition — shown to users AND injected at runtime
    helperCode: text('helperCode'), // Serializers, utilities — injected at runtime only, never shown to users
    testCode: text('testCode').notNull(), // Code to validate the template works
    testExpectedOutput: text('testExpectedOutput').notNull(), // Expected output from test code
    lastValidatedAt: timestamp('lastValidatedAt', { mode: 'date' }), // Timestamp of last successful validation
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('TestTemplateCode_templateId_language_unique').on(
      table.templateId,
      table.language,
    ),
    index('TestTemplateCode_templateId_idx').on(table.templateId),
  ],
)

/**
 * TestConfigurationTemplate junction table - Links templates to test configurations
 *
 * A test configuration can use multiple templates, and order matters
 * (templates are injected in the specified order).
 */
export const testConfigurationTemplate = pgTable(
  'TestConfigurationTemplate',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    testConfigurationId: uuid('testConfigurationId')
      .notNull()
      .references(() => testConfiguration.id, { onDelete: 'cascade' }),
    testTemplateId: uuid('testTemplateId')
      .notNull()
      .references(() => testTemplate.id, { onDelete: 'cascade' }),
    order: integer('order').notNull().default(0), // Order in which templates are injected
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('TestConfigurationTemplate_config_template_unique').on(
      table.testConfigurationId,
      table.testTemplateId,
    ),
    index('TestConfigurationTemplate_configId_idx').on(
      table.testConfigurationId,
    ),
    index('TestConfigurationTemplate_templateId_idx').on(table.testTemplateId),
  ],
)

/**
 * TestConfigurationCustomCode table - Problem-specific custom code
 *
 * Stores problem-specific custom setup and verification code, normalized by language.
 * - setupCode: Transforms inputs before calling user function
 * - verificationCode: Validates outputs (for non-standard comparison)
 */
export const testConfigurationCustomCode = pgTable(
  'TestConfigurationCustomCode',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    testConfigurationId: uuid('testConfigurationId')
      .notNull()
      .references(() => testConfiguration.id, { onDelete: 'cascade' }),
    language: text('language').notNull(), // 'python' | 'javascript'
    setupCode: text('setupCode'), // Optional: transforms inputs before calling user function
    verificationCode: text('verificationCode'), // Optional: validates outputs
    testCode: text('testCode'), // Code to validate custom logic works
    testExpectedOutput: text('testExpectedOutput'), // Expected output from test code
    lastValidatedAt: timestamp('lastValidatedAt', { mode: 'date' }), // Timestamp of last successful validation
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('TestConfigurationCustomCode_config_language_unique').on(
      table.testConfigurationId,
      table.language,
    ),
    index('TestConfigurationCustomCode_configId_idx').on(
      table.testConfigurationId,
    ),
  ],
)

// =============================================================================
// TEST TEMPLATE RELATIONS
// =============================================================================

export const testTemplateRelations = relations(testTemplate, ({ many }) => ({
  code: many(testTemplateCode),
  configurationLinks: many(testConfigurationTemplate),
}))

export const testTemplateCodeRelations = relations(
  testTemplateCode,
  ({ one }) => ({
    template: one(testTemplate, {
      fields: [testTemplateCode.templateId],
      references: [testTemplate.id],
    }),
  }),
)

export const testConfigurationTemplateRelations = relations(
  testConfigurationTemplate,
  ({ one }) => ({
    testConfiguration: one(testConfiguration, {
      fields: [testConfigurationTemplate.testConfigurationId],
      references: [testConfiguration.id],
    }),
    template: one(testTemplate, {
      fields: [testConfigurationTemplate.testTemplateId],
      references: [testTemplate.id],
    }),
  }),
)

export const testConfigurationCustomCodeRelations = relations(
  testConfigurationCustomCode,
  ({ one }) => ({
    testConfiguration: one(testConfiguration, {
      fields: [testConfigurationCustomCode.testConfigurationId],
      references: [testConfiguration.id],
    }),
  }),
)

// =============================================================================
// RECIPE TABLES
// =============================================================================

/**
 * Recipe table - Reusable coding recipes/patterns
 *
 * Stores reusable coding recipes that demonstrate common patterns,
 * techniques, or building blocks used across problems.
 */
export const recipe = pgTable('Recipe', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: text('name').notNull(), // Human-readable name (e.g., "Binary Search on Sorted Array")
  slug: text('slug').notNull().unique(), // URL-safe identifier (e.g., "binary-search-sorted-array")
  shortDescription: text('shortDescription'), // One-line summary
  descriptionDetails: text('descriptionDetails'), // Multi-line markdown walkthrough
  pseudocode: text('pseudocode'), // Markdown pseudocode block
  order: integer('order').notNull().default(0), // For ordering in UI
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  deletedAt: timestamp('deletedAt', { mode: 'date' }),
})

/**
 * RecipeImplementation table - Language-specific code for recipes
 *
 * Stores the actual code for each recipe, normalized by language.
 * Each recipe can have implementations for multiple languages (Python, JavaScript).
 */
export const recipeImplementation = pgTable(
  'RecipeImplementation',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    recipeId: uuid('recipeId')
      .notNull()
      .references(() => recipe.id, { onDelete: 'cascade' }),
    language: text('language').notNull(), // 'python' | 'javascript'
    code: text('code').notNull(), // The actual recipe code
    testCode: text('testCode').notNull(), // Code to validate the recipe works
    testExpectedOutput: text('testExpectedOutput').notNull(), // Expected output from test code
    lastValidatedAt: timestamp('lastValidatedAt', { mode: 'date' }), // Timestamp of last successful validation
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('RecipeImplementation_recipeId_language_unique').on(
      table.recipeId,
      table.language,
    ),
    index('RecipeImplementation_recipeId_idx').on(table.recipeId),
  ],
)

/**
 * ApproachRecipe junction table - Links approaches to recipes for querying
 *
 * Allows efficient queries like:
 * - "Find all approaches using recipe X"
 * - "Find all recipes for approach Y"
 */
export const approachRecipe = pgTable(
  'ApproachRecipe',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    approachId: uuid('approachId')
      .notNull()
      .references(() => approach.id, { onDelete: 'cascade' }),
    recipeId: uuid('recipeId')
      .notNull()
      .references(() => recipe.id, { onDelete: 'cascade' }),
    order: integer('order').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  },
  (table) => [
    unique('ApproachRecipe_approach_recipe_unique').on(
      table.approachId,
      table.recipeId,
    ),
    index('ApproachRecipe_approachId_idx').on(table.approachId),
    index('ApproachRecipe_recipeId_idx').on(table.recipeId),
  ],
)

// =============================================================================
// RECIPE RELATIONS
// =============================================================================

export const recipeRelations = relations(recipe, ({ many }) => ({
  implementations: many(recipeImplementation),
  approachRecipes: many(approachRecipe),
}))

export const recipeImplementationRelations = relations(
  recipeImplementation,
  ({ one }) => ({
    recipe: one(recipe, {
      fields: [recipeImplementation.recipeId],
      references: [recipe.id],
    }),
  }),
)

export const approachRecipeRelations = relations(approachRecipe, ({ one }) => ({
  approach: one(approach, {
    fields: [approachRecipe.approachId],
    references: [approach.id],
  }),
  recipe: one(recipe, {
    fields: [approachRecipe.recipeId],
    references: [recipe.id],
  }),
}))

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type Problem = typeof problem.$inferSelect
export type NewProblem = typeof problem.$inferInsert
export type DifficultyLevel = typeof difficultyLevel.$inferSelect
export type NewDifficultyLevel = typeof difficultyLevel.$inferInsert
export type Approach = typeof approach.$inferSelect
export type NewApproach = typeof approach.$inferInsert
export type Solution = typeof solution.$inferSelect
export type NewSolution = typeof solution.$inferInsert
export type SolutionComplexity = typeof solutionComplexity.$inferSelect
export type NewSolutionComplexity = typeof solutionComplexity.$inferInsert
export type ApproachComplexity = typeof approachComplexity.$inferSelect
export type NewApproachComplexity = typeof approachComplexity.$inferInsert
export type Company = typeof company.$inferSelect
export type NewCompany = typeof company.$inferInsert
export type Source = typeof source.$inferSelect
export type NewSource = typeof source.$inferInsert
export type ProblemCompany = typeof problemCompany.$inferSelect
export type NewProblemCompany = typeof problemCompany.$inferInsert
export type Hint = typeof hint.$inferSelect
export type NewHint = typeof hint.$inferInsert
export type ProblemConstraint = typeof problemConstraint.$inferSelect
export type NewProblemConstraint = typeof problemConstraint.$inferInsert
export type Clarification = typeof clarification.$inferSelect
export type NewClarification = typeof clarification.$inferInsert
export type ApproachHint = typeof approachHint.$inferSelect
export type NewApproachHint = typeof approachHint.$inferInsert
export type ProblemSource = typeof problemSource.$inferSelect
export type NewProblemSource = typeof problemSource.$inferInsert
export type ProblemVariant = typeof problemVariant.$inferSelect
export type NewProblemVariant = typeof problemVariant.$inferInsert
export type ProblemSeries = typeof problemSeries.$inferSelect
export type NewProblemSeries = typeof problemSeries.$inferInsert
export type ProblemSeriesMember = typeof problemSeriesMember.$inferSelect
export type NewProblemSeriesMember = typeof problemSeriesMember.$inferInsert
export type ProblemFollowUp = typeof problemFollowUp.$inferSelect
export type NewProblemFollowUp = typeof problemFollowUp.$inferInsert
export type FollowUpPrereq = typeof followUpPrereq.$inferSelect
export type NewFollowUpPrereq = typeof followUpPrereq.$inferInsert
export type FollowUpApproach = typeof followUpApproach.$inferSelect
export type NewFollowUpApproach = typeof followUpApproach.$inferInsert
export type ProblemRelationship = typeof problemRelationship.$inferSelect
export type NewProblemRelationship = typeof problemRelationship.$inferInsert
export type TestConfiguration = typeof testConfiguration.$inferSelect
export type NewTestConfiguration = typeof testConfiguration.$inferInsert
export type TestCase = typeof testCase.$inferSelect
export type NewTestCase = typeof testCase.$inferInsert
export type TestTemplate = typeof testTemplate.$inferSelect
export type NewTestTemplate = typeof testTemplate.$inferInsert
export type TestTemplateCode = typeof testTemplateCode.$inferSelect
export type NewTestTemplateCode = typeof testTemplateCode.$inferInsert
export type TestConfigurationTemplate =
  typeof testConfigurationTemplate.$inferSelect
export type NewTestConfigurationTemplate =
  typeof testConfigurationTemplate.$inferInsert
export type TestConfigurationCustomCode =
  typeof testConfigurationCustomCode.$inferSelect
export type NewTestConfigurationCustomCode =
  typeof testConfigurationCustomCode.$inferInsert
export type Recipe = typeof recipe.$inferSelect
export type NewRecipe = typeof recipe.$inferInsert
export type RecipeImplementation = typeof recipeImplementation.$inferSelect
export type NewRecipeImplementation = typeof recipeImplementation.$inferInsert
export type ApproachRecipe = typeof approachRecipe.$inferSelect
export type NewApproachRecipe = typeof approachRecipe.$inferInsert
